import {
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { SingleSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { TextingIndicator } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import {
  red,
  warning,
} from '@fortress-technology-solutions/fortress-component-library/design';
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import { createActivity } from '../../../components/CreateActivityModal';
import { editCompletedActivity } from '../../../components/EditCompletedActivityModal';
import { editPendingActivity } from '../../../components/EditPendingActivityModal';
import useActivityTypes from '../../../hooks/data-fetching/useActivityTypes';
import { useFetchConversations } from '../../../hooks/data-fetching/useFetchConversations';
import useProspectAssignees from '../../../hooks/data-fetching/useProspectAssignees';
import useProspectStatuses from '../../../hooks/data-fetching/useProspectStatuses';
import useHasPermission from '../../../hooks/useHasPermission';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import ActivityService from '../../../services/activityService';
import ProspectProfileService from '../../../services/prospectProfileService';
import { exportAndDownloadCSV } from '../../../utils/csv-helpers';
import {
  getUrlWithSelectedPropertyId,
  navigateToUrlWithSelectedPropertyId,
} from '../../../utils/navigation-helpers';
import { formatNBedsArr } from '../../../utils/prospectPreferences-helpers';
import {
  conversationIndicatorStatus,
  formatTextingCounts,
} from '../../../utils/texting';
import { useAppFlags } from '../../../utils/featureFlags';
import {
  buildProspectActivity,
  extractLeadSourcesString,
  extractUniqueLeadSources,
  getFiltersToClear,
  parseCommaDelimited,
} from './utils';
import { phoneFormatter } from '../../../utils/redux-form-helper';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../../hooks/useActionsFirstColumnOrder';
import actionMenuMessages from '../ActionsMenu/messages';
import ChangeStatusModal from '../ChangeStatusModal';
import Status from '../Status';
import messages from '../messages';
import { HEADERS, PROPERTY_PATH_MAP } from './constants';
import { TAB_KEYS } from '../../ResidentProfile/constants';
import { isNextActivityLate } from '../../../utils/date-helpers';
import { adaptHeaders } from 'utils/table-helpers';

export function useManageProspects({ intl, store }) {
  const selectedProperty = useSelectedProperty();
  const name = useUniqueTableName('manage-prospects');
  const hasProspectAssignPermission = useHasPermission('prospect-assign');
  const hasProspectUpdatePermission = useHasPermission('prospect-update');
  const hasActivityCreatePermission = useHasPermission('activity-create');
  const hasActivityReadPermission = useHasPermission('activity-read');
  const hasActivityUpdatePermission = useHasPermission('activity-update');
  const hasActivityDeletePermission = useHasPermission('activity-delete');
  const isEditActivityDisabled =
    (hasActivityReadPermission &&
      hasActivityUpdatePermission &&
      hasActivityDeletePermission) === false;
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const {
    data,
    isLoading,
    refetch: refreshTable,
  } = useManageProspectsTableData({ getAllStatuses: showAllStatuses });
  const { prospectAssignees: users } = useProspectAssignees();
  const { prospectStatuses } = useProspectStatuses();
  const { activityTypes } = useActivityTypes();
  const appFlags = useAppFlags();

  const { householdConversationsStatusDictionary, showTextingColumn } =
    useTextingColumn({ prospects: data ?? [], leslieEnabled: appFlags.leslie });

  const statusList = useMemo(
    () =>
      prospectStatuses.map((prospectStatus) => ({
        value: prospectStatus.id,
        text: prospectStatus.name,
        color: prospectStatus.color,
        requiresNote: prospectStatus.requiresNote,
        isDefault: prospectStatus.isDefault,
      })),
    [prospectStatuses],
  );
  const activityTypesList = useMemo(() => {
    const list = activityTypes
      .filter((at) => at.automatedOnly === false)
      .map((activityType) => ({
        value: activityType.id,
        text: activityType.name,
        canBeOverlapped: activityType.canBeOverlapped,
        completionStatusOptions: activityType.completionStatusOptions,
        disabled: false,
      }));

    list.unshift({
      value: 'default',
      text: intl.formatMessage(messages.chooseOption),
      completionStatusOptions: [{ id: '' }],
      disabled: true,
      showForRecord: true,
    });

    return list;
  }, [activityTypes, intl]);

  const assigneeOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        text: `${u.firstName} ${u.lastName}`,
      })),
    [users],
  );

  const mappedData = useMemo(
    () =>
      data?.map((d) => {
        const assignedToName = d.assignedTo
          ? `${d.assignedTo.firstName} ${d.assignedTo.lastName}`
          : null;
        const nbf = formatNBedsArr(
          d.prospectPreferences?.nBedsArr,
          d.prospectPreferences?.nBeds,
        );

        // Extract comma-separated lead sources string
        const allLeadSources = extractLeadSourcesString(d);

        return {
          ...d,
          prospectName: `${d.lastName}, ${d.firstName}`,
          nBedsFormatted: Array.isArray(nbf) ? nbf.join(', ') : nbf,
          assignedToName,
          texting: householdConversationsStatusDictionary[d.id] ?? null,
          allLeadSources,
        };
      }),
    [data, householdConversationsStatusDictionary],
  );

  const filterOptions = useFilterOptions({ prospects: mappedData });

  const headers = useHeaders({
    showTextingColumn,
    filterOptions,
    showAllStatuses,
    flags: appFlags,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: mappedData,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  // ENG-21372: a saved selection the current options no longer offer is
  // cleared, so the table does not sit empty with no visible cause.
  useEffect(() => {
    getFiltersToClear({
      prospects: mappedData,
      filterState,
      filterOptions,
    }).forEach((id) => handleFilterChange(id, []));
  }, [mappedData, filterState, filterOptions, handleFilterChange]);

  const handleAssigneeChange = useCallback(
    async (assigneeId, prospect) => {
      const service = new ProspectProfileService();
      try {
        await service.assign(
          {
            id: prospect.id,
            assignedToId: assigneeId,
            propertyId: selectedProperty?.id,
          },
          selectedProperty.organizationId,
        );

        toastr.success(
          intl.formatMessage(messages.successDescription),
          intl.formatMessage(messages.successHeader),
          {
            showCloseButton: true,
            removeOnHover: true,
          },
        );
        refreshTable();
      } catch (err) {
        toastr.error(err.toString(), intl.formatMessage(messages.errorHeader), {
          showCloseButton: true,
          removeOnHover: true,
        });
      }
    },
    [selectedProperty, intl, refreshTable],
  );

  const handleChangeStatus = useCallback(
    async ({ prospect, newStatus, notes }) => {
      try {
        const service = new ProspectProfileService();

        await service.changeStatus(
          prospect,
          newStatus,
          notes,
          selectedProperty.organizationId,
        );

        toastr.success(
          intl.formatMessage(messages.successDescription),
          intl.formatMessage(messages.successHeader),
          {
            showCloseButton: true,
            removeOnHover: true,
          },
        );
        refreshTable();
      } catch (err) {
        toastr.error(err.toString(), intl.formatMessage(messages.errorHeader), {
          showCloseButton: true,
          removeOnHover: true,
        });
      }
    },
    [selectedProperty, intl, refreshTable],
  );

  const handleCreateActivity = useCallback(
    async ({ activity }) => {
      try {
        const service = new ActivityService();

        await service.save(
          buildProspectActivity(activity),
          selectedProperty.organizationId,
        );

        toastr.success(
          intl.formatMessage(messages.successActivityDescription),
          intl.formatMessage(messages.successHeader),
          {
            showCloseButton: true,
            removeOnHover: true,
          },
        );
        refreshTable();
      } catch (err) {
        toastr.error(err.toString(), intl.formatMessage(messages.errorHeader), {
          showCloseButton: true,
          removeOnHover: true,
        });
      }
    },
    [selectedProperty, intl, refreshTable],
  );

  const handleCreateActivityClick = useCallback(
    (prospect) => {
      createActivity({
        store,
        intl,
        prospect,
        activityTypesList,
        assigneeList: assigneeOptions,
        stage: 'prospect',
        urlId: prospect.id,
        showViewProfileBtn: true,
      }).then((data) => handleCreateActivity({ activity: data }));
    },
    [intl, store, activityTypesList, assigneeOptions, handleCreateActivity],
  );

  const updateProspectActivity = useCallback(
    async (activity) => {
      try {
        const service = new ActivityService();

        // FES-1762: send only the fields the backend's ActivitySchema declares.
        // The table rows carry the whole activity record, and the endpoint
        // rejects any other field.
        const payload = pick(activity, [
          'startTime',
          'endTime',
          'notes',
          'assignedToId',
          'propertyId',
          'householdId',
          'contactId',
          'activityTypeId',
          'activityCompletionStatusId',
          'allDay',
          'isAutomated',
          'prospectId',
          'customerStatus',
        ]);

        await service.edit(
          payload,
          activity.id,
          selectedProperty.organizationId,
        );

        refreshTable();

        toastr.success(
          intl.formatMessage(messages.successEditActivityDescription),
          intl.formatMessage(messages.successHeader),
          {
            showCloseButton: true,
            removeOnHover: true,
          },
        );
      } catch (err) {
        toastr.error(err.toString(), intl.formatMessage(messages.errorHeader), {
          showCloseButton: true,
          removeOnHover: true,
        });
      }
    },
    [intl, refreshTable, selectedProperty],
  );

  const deleteProspectActivity = useCallback(
    async (activity) => {
      try {
        const service = new ActivityService();

        await service.delete(
          omit(activity, ['prospect']),
          selectedProperty.organizationId,
        );

        refreshTable();

        toastr.success(
          intl.formatMessage(messages.successDeleteActivityDescription),
          intl.formatMessage(messages.successHeader),
          {
            showCloseButton: true,
            removeOnHover: true,
          },
        );
      } catch (err) {
        toastr.error(err.toString(), intl.formatMessage(messages.errorHeader), {
          showCloseButton: true,
          removeOnHover: true,
        });
      }
    },
    [intl, refreshTable, selectedProperty],
  );

  const handleEditActivity = useCallback(
    ({ activity, prospect }) => {
      const stageInfo = {
        currentStage: 'prospect',
        urlId: activity.prospectId,
      };

      activity.activityType = activityTypes.filter(
        (at) => at.id === activity.activityTypeId,
      )[0];

      if (activity.activityType) {
        activity.completionStatus =
          activity.activityType.completionStatusOptions.filter(
            (s) => s.id === activity.activityCompletionStatusId,
          )[0];
      }

      activity.prospect = prospect;

      const editPromise = activity.activityCompletionStatusId
        ? editCompletedActivity(store, intl, activity, stageInfo)
        : editPendingActivity(
            store,
            intl,
            activity,
            activityTypesList,
            assigneeOptions,
            activity.prospect,
            stageInfo,
          );

      const editFunction = () => handleCreateActivityClick(prospect);

      editPromise.then((data) => {
        const editedActivity = buildProspectActivity({
          ...data,
          customerStatus: activity.customerStatus,
        });

        if (data.saveAndClose) {
          updateProspectActivity(editedActivity);
        } else if (data.saveAndAddNew) {
          updateProspectActivity(editedActivity);
          editFunction();
        } else {
          deleteProspectActivity(data);
        }
      });
    },
    [
      intl,
      store,
      activityTypesList,
      handleCreateActivityClick,
      updateProspectActivity,
      deleteProspectActivity,
      activityTypes,
      assigneeOptions,
    ],
  );

  const getNextActivityColor = useCallback((activity) => {
    if (!activity || isEmpty(activity.startTime)) return;

    const currentTime = new Date();
    const startTime = new Date(activity.startTime);

    if (startTime.getTime() < currentTime.getTime()) return red.dark;
  }, []);

  const rows = useMemo(
    () =>
      sortedAndFilteredResults.map((result) => {
        const lastActivity = result.lastActivity?.startTime;
        const lastActivityFormatted = lastActivity
          ? formatDateDisplayLocal(lastActivity)
          : '';
        const nextActivity = result.nextActivity?.startTime;
        const nextActivityFormatted = nextActivity
          ? formatDateDisplayLocal(nextActivity)
          : '';
        const moveInDate = result.prospectPreferences?.moveInDateFrom;
        const color = result.currentProspectStatus?.color ?? 'gray';
        const statusName = result.currentProspectStatus?.name ?? '';
        const assignee = users.find((u) => u.id === result.assignedToId);
        const backgroundColor = assignee ? null : warning.lighter;
        const texting = result.texting ?? {
          TOTAL: 0,
        };
        const urlToProspectProfile = getUrlWithSelectedPropertyId(
          `/prospect/${result.id}`,
        );
        const _assigneeOptions =
          assignee || !result.assignedToId
            ? assigneeOptions
            : [
                ...assigneeOptions,
                {
                  value: result.assignedToId,
                  text: `${result.assignedTo?.firstName} ${result.assignedTo?.lastName}`,
                },
              ];

        return {
          id: result?.id,
          sx: {
            backgroundColor,
          },
          status: {
            value: (
              <Stack direction="row" spacing={1} alignItems="center">
                <Status color={color} noMargin />
                <Typography fontSize={12}>{statusName}</Typography>
              </Stack>
            ),
          },
          prospectName: {
            variant: 'link',
            to: urlToProspectProfile,
            value: `${result.lastName}, ${result.firstName}`,
          },
          email: {
            variant: 'email',
            href: `mailto:${result.emailAddress}`,
            value: result.emailAddress,
          },
          phoneNumber: {
            variant: 'phoneNumber',
            value: result.phoneNumber,
          },
          texting: {
            variant: texting.TOTAL > 0 ? 'link' : undefined,
            value: <TextingIndicator {...result.texting} />,
            to:
              texting.TOTAL > 0
                ? `${urlToProspectProfile}?tab=${TAB_KEYS.TEXTING}`
                : undefined,
          },
          prospectCreated: {
            variant: 'date',
            value: result.createdAt,
          },
          lastActivity: {
            variant: 'button',
            onClick: () => {
              handleEditActivity({
                activity: result.lastActivity,
                prospect: result,
              });
            },
            disabled: isEditActivityDisabled,
            value: lastActivityFormatted,
          },
          nextActivity: {
            variant: 'button',
            onClick: () =>
              handleEditActivity({
                activity: result.nextActivity,
                prospect: result,
              }),
            disabled: isEditActivityDisabled,
            value: result?.nextActivity?.startTime
              ? nextActivityFormatted
              : null,
            ...(isNextActivityLate(result.nextActivity) && {
              ButtonProps: {
                sx: {
                  color: 'error.main',
                },
              },
            }),
          },
          moveInDate: {
            variant: 'date',
            value: moveInDate,
          },
          nBeds: {
            variant: 'description',
            value: result.nBedsFormatted,
          },
          assignedTo: {
            value: (
              <SingleSelect
                options={_assigneeOptions}
                value={result.assignedToId}
                fullWidth
                sx={{ height: 40 }}
                disabled={!hasProspectAssignPermission}
                onChange={(value) => {
                  handleAssigneeChange(value, result);
                }}
                placeholder={
                  !result.assignedToId
                    ? intl.formatMessage(messages.unassigned)
                    : undefined
                }
              />
            ),
          },
          actions: {
            variant: 'menu',
            name: 'actions',
            iconName: 'BoltIcon',
            color: 'primary',
            closeOnClick: true,
            options: [
              {
                disabled: !hasProspectUpdatePermission,
                value: 'changeStatus',
                text: <FormattedMessage {...actionMenuMessages.changeStatus} />,
                onClick: () => {
                  ChangeStatusModal(
                    store,
                    intl,
                    statusList,
                    result.prospectStatusId,
                  ).then(({ newStatus, notes }) => {
                    handleChangeStatus({
                      prospect: result,
                      newStatus,
                      notes,
                    });
                  });
                },
              },
              {
                disabled: !hasActivityCreatePermission,
                value: 'createActivity',
                text: (
                  <FormattedMessage {...actionMenuMessages.createActivity} />
                ),
                onClick: () => handleCreateActivityClick(result),
              },
            ],
          },
          leadSource: {
            value: result.allLeadSources,
          },
        };
      }),
    [
      sortedAndFilteredResults,
      users,
      assigneeOptions,
      hasProspectAssignPermission,
      hasProspectUpdatePermission,
      hasActivityCreatePermission,
      handleAssigneeChange,
      intl,
      store,
      statusList,
      handleChangeStatus,
      handleCreateActivityClick,
      isEditActivityDisabled,
      handleEditActivity,
      getNextActivityColor,
    ],
  );

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const csvRows = useMemo(() => {
    return sortedAndFilteredResults?.map((result) => {
      const statusName = result.currentProspectStatus?.name ?? '';
      return {
        status: {
          value: statusName,
        },
        prospectName: {
          value: `${result.lastName}, ${result.firstName}`,
        },
        phoneNumber: {
          value: phoneFormatter(result.phoneNumber),
        },
        texting: {
          value: formatTextingCounts(result.texting),
        },
        email: {
          value: result.emailAddress,
        },
        prospectCreated: {
          variant: 'date',
          value: result.createdAt,
        },
        lastActivity: {
          variant: 'date',
          value: result.lastActivity?.startTime,
        },
        nextActivity: {
          variant: 'date',
          value: result.nextActivity?.startTime,
        },
        moveInDate: {
          variant: 'date',
          value: result.prospectPreferences?.moveInDateFrom,
        },
        nBeds: {
          value: result.nBedsFormatted,
        },
        assignedTo: {
          value: result.assignedTo
            ? `${result.assignedTo.firstName} ${result.assignedTo.lastName}`
            : intl.formatMessage(messages.unassigned),
        },
        leadSource: {
          value: result.allLeadSources,
        },
      };
    });
  }, [sortedAndFilteredResults, intl]);

  const handleCSVButtonClick = useCallback(() => {
    const hasAnyFilters =
      Object.keys(filterState)?.length ||
      Object.keys(dateState)?.length ||
      Object.keys(searchState)?.length;

    exportAndDownloadCSV({
      excludedHeaders: ['actions'].concat(showTextingColumn ? [] : ['texting']),
      filteredHeaders,
      fileName: 'ManageProspects.csv',
      hasAnyFilters,
      rows: csvRows,
    });

    return true;
  }, [
    filterState,
    dateState,
    searchState,
    csvRows,
    filteredHeaders,
    showTextingColumn,
  ]);

  function onNewProspectClick() {
    navigateToUrlWithSelectedPropertyId('/prospect');
  }

  return {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    name,
    searchState,
    sortedAndFilteredResults,
    isLoading,
    rows,
    count: rows.length ?? 0,
    totalCount: data?.length ?? 0,
    onCSVButtonClick: handleCSVButtonClick,
    onNewProspectClick,
    showAllStatuses,
    setShowAllStatuses,
  };
}

function useManageProspectsTableData({ getAllStatuses }) {
  const selectedProperty = useSelectedProperty();
  const queryKey = [
    'getManageProspectsTableData',
    selectedProperty?.id,
    selectedProperty?.organizationId,
    getAllStatuses,
  ];
  const { data, isLoading, refetch } = useQuery(
    queryKey,
    () => {
      const service = new ProspectProfileService();
      return service
        .getAllProspects({
          organizationId: selectedProperty?.organizationId,
          propertyId: selectedProperty?.id,
          getAllStatuses,
        })
        .then((response) => response?.results);
    },
    {
      staleTime: 60000,
      refetchOnWindowFocus: true,
    },
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

function useTextingColumn({ prospects, leslieEnabled }) {
  const selectedProperty = useSelectedProperty();
  const prospectIds = useMemo(
    () => prospects.map((p) => p.id).flat(),
    [prospects],
  );

  const hasCommunicationCreatePermission = useHasPermission(
    'communication-create',
  );
  const showTextingColumn =
    selectedProperty.isTwoWayCommunicationActive &&
    hasCommunicationCreatePermission;

  const [conversations] = useFetchConversations(prospectIds, showTextingColumn);

  const householdConversationsStatusDictionary = useMemo(() => {
    const conversationsDictionary = keyBy(
      conversations,
      (c) => c.recipient.prospectId,
    );

    return prospects.reduce((acc, prospect) => {
      const conversation = conversationsDictionary[prospect.id] ?? {};
      const status = conversationIndicatorStatus(conversation, leslieEnabled);
      const householdConversationsStatus = {
        NEEDS_HELP: status === 'NEEDS_HELP' ? 1 : 0,
        NEW: status === 'NEW' ? 1 : 0,
        UNRESOLVED: status === 'UNRESOLVED' ? 1 : 0,
        RESOLVED: status === 'RESOLVED' ? 1 : 0,
      };

      householdConversationsStatus.TOTAL =
        householdConversationsStatus.NEEDS_HELP +
        householdConversationsStatus.NEW +
        householdConversationsStatus.UNRESOLVED;

      acc[prospect.id] = householdConversationsStatus;

      return acc;
    }, {});
  }, [conversations, prospects, leslieEnabled]);

  return {
    householdConversationsStatusDictionary,
    showTextingColumn,
  };
}

function useHeaders({
  showTextingColumn,
  filterOptions,
  showAllStatuses,
  flags,
}) {
  return useMemo(() => {
    const headers = [];

    HEADERS({ showTextingColumn, showAllStatuses }).forEach((header) => {
      if (filterOptions[`${header.id}Options`])
        header.filterOptions = filterOptions[`${header.id}Options`];

      if (header.filterOptions) {
        // Adapt the filtering to set options based on enabled function
        header.filterOptions = header.filterOptions.filter((o) =>
          o.enabled ? o.enabled({ flags }) : true,
        );
      }

      headers.push(header);
    });

    adaptHeaders(headers, { flags });

    return headers;
  }, [showTextingColumn, filterOptions, showAllStatuses, flags.leslie]);
}

export function useFilterOptions({ prospects }) {
  return useMemo(() => {
    // FES-1244: every option list stays an array, including the shape returned
    // before the prospects load. The filter-validation effect maps over them on
    // the first render, and a Set has no map.
    const options = {
      nBedsOptions: [],
      statusOptions: [],
      leadSourceOptions: [],
    };

    if (!prospects) return options;

    prospects.forEach(
      ({ currentProspectStatus: prospectStatus, nBedsFormatted }) => {
        if (prospectStatus) options.statusOptions.push(prospectStatus);
        if (nBedsFormatted) {
          const values = parseCommaDelimited(nBedsFormatted);
          values.forEach((v) => {
            options.nBedsOptions.push(v);
          });
        }
      },
    );

    options.statusOptions = Array.from(uniqBy(options.statusOptions, 'id'))
      .sort()
      .map((status) => ({
        text: status?.name,
        value: status?.id,
      }));

    options.nBedsOptions = Array.from(new Set(options.nBedsOptions))
      .sort()
      .map((bedsBaths) => ({
        text: bedsBaths,
        value: bedsBaths,
      }));

    // Extract unique lead sources using utility function
    options.leadSourceOptions = Array.from(extractUniqueLeadSources(prospects))
      .sort()
      .map((leadSource) => ({
        text: leadSource,
        value: leadSource,
      }));

    return options;
  }, [prospects]);
}
